import { StopNew } from "../../../ducks/data/route-with-stops/types";
import HomeIcon from "@mui/icons-material/Home";
import "./stop-component.scss";
import { ReactNode, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectShouldShowEditCargoButtons } from "../../../ducks/auth/selectors";
import Button from "@mui/material/Button";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditAddressDialog from "../../SessionsPage/StopPage/edit-dialogs/EditAddressDialog";
import { useRoutePageContext } from "../RoutePageContext";
import { isMobile } from "../../../helpers/mobile";
import { Capacitor } from "@capacitor/core";
import { AppLauncher } from "@capacitor/app-launcher";

const StopAddress = ({ stop }: { stop: StopNew }) => {
  const [shouldShowEditAddressDialog, setShouldShowEditAddressDialog] =
    useState(false);
  const shouldShowEditCargoButtons = useSelector(
    selectShouldShowEditCargoButtons
  );

  const address = useMemo(() => {
    let val = [stop.place.address, stop.place.city, stop.place.postcode]
      .filter(Boolean)
      .join(", ");
    if (!val) {
      val = stop.place.placeName || "";
    }
    return val;
  }, [stop]);

  const { reloadData } = useRoutePageContext();

  return (
    <>
      {/* Kept outside StopAddressParentComponent: React events bubble through
          portals, so clicks inside the dialog would trigger its maps onClick */}
      {shouldShowEditAddressDialog && (
        <EditAddressDialog
          stopId={stop.id}
          cargoId={stop.cargoId}
          onClose={() => {
            reloadData();
            setShouldShowEditAddressDialog(false);
          }}
        />
      )}
      <StopAddressParentComponent stop={stop}>
        <div className="stop-component">
          <HomeIcon className="stop-component-icon" />
          <div className="flex items-center justify-between w-full">
            <div className="flex-grow">{address}</div>
            {shouldShowEditCargoButtons && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShouldShowEditAddressDialog(true);
                }}
              >
                <EditOutlinedIcon />
              </Button>
            )}
          </div>
        </div>
      </StopAddressParentComponent>
    </>
  );
};

const StopAddressParentComponent = ({
  children,
  stop,
}: {
  children: ReactNode;
  stop: StopNew;
}) => {
  const url = useMemo(() => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${stop.coord.lat},${stop.coord.lon}`
    )}`;
  }, [stop]);

  const onMobileClick = async () => {
    const platform = Capacitor.getPlatform();
    if (platform === "ios") {
      const probeScheme = "comgooglemaps://";
      const gmapsAppUrl = `comgooglemaps://?q=${stop.coord.lat},${stop.coord.lon}`;
      const appStoreUrl = "itms-apps://itunes.apple.com/app/id585027354";

      try {
        const can = await AppLauncher.canOpenUrl({ url: probeScheme });
        if (can.value) {
          await AppLauncher.openUrl({ url: gmapsAppUrl });
        } else {
          await AppLauncher.openUrl({ url: appStoreUrl });
        }
      } catch {
        // Fallback to App Store if capability check fails unexpectedly
        try {
          await AppLauncher.openUrl({ url: appStoreUrl });
        } catch {}
      }
      return;
    }

    // Android: prefer geo intent
    const geoIntent = `geo:${stop.coord.lat},${stop.coord.lon}?q=${stop.coord.lat},${stop.coord.lon}`;
    try {
      const can = await AppLauncher.canOpenUrl({ url: "geo:" });
      if (can.value) {
        await AppLauncher.openUrl({ url: geoIntent });
      } else {
        // Last resort: http URL (device will prompt app if available)
        await AppLauncher.openUrl({
          url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            `${stop.coord.lat},${stop.coord.lon}`
          )}`,
        });
      }
    } catch {
      // no-op
    }
  };

  return isMobile ? (
    <div onClick={onMobileClick}>{children}</div>
  ) : (
    <a style={{ textDecoration: "none" }} href={url}>
      {children}
    </a>
  );
};

export default StopAddress;
